import React, { useEffect, useMemo, useRef, useState } from 'react';
import { stripSerial, buzzSuccess, buzzError } from '../../utils/scan';

// Hardware upgrades fitted to devices after they left the factory. Each device
// records one entry per part in d.hwMods, keyed by id: { date, by }. A missing key
// means the part has not been changed yet. Adding a part here adds it to the scan
// box, the stock badges, the stock filter and the XLSX export.
export const HW_MODS = [
  { id: 'klickFast', label: 'Klick Fast', short: 'KF' },
  { id: 'usbC', label: 'USB-C charging', short: 'USB-C' },
];
export const hwModDone = (d, id) => !!(d && d.hwMods && d.hwMods[id]);

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB') : '');

const STATE_STYLE = {
  ready: { label: 'To update', c: 'var(--success-text)', bg: 'var(--success-fill)', b: 'var(--success-border)' },
  retired: { label: 'Retired', c: 'var(--warning-text)', bg: 'var(--warning-fill)', b: 'var(--warning-border)' },
  done: { label: 'Already done', c: 'var(--text-muted)', bg: 'var(--surface-inset)', b: 'var(--border-default)' },
  unknown: { label: 'Unknown', c: 'var(--danger-text)', bg: 'var(--danger-fill)', b: 'var(--danger-border)' },
};

// Scan a batch of devices and record which parts were changed on them. Opened from
// the stock toolbar (empty) or the selection bar (presetIds = the ticked devices).
function HwModScanModal({ open, onClose, st, setSt, audit, account, canClear, presetIds }) {
  const [parts, setParts] = useState(HW_MODS.map(m => m.id));
  const [mode, setMode] = useState('mark'); // 'mark' | 'clear'
  const [serials, setSerials] = useState([]);
  const [input, setInput] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [feedback, setFeedback] = useState(null); // { ok, text }
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null); // 'list' | 'result'
  const inputRef = useRef(null);

  const devices = st.devices || [];

  // Reset on every open so a previous session's list never carries over.
  useEffect(() => {
    if (!open) return;
    const pre = (presetIds || []).map(id => devices.find(d => d.id === id)).filter(Boolean).map(d => d.serial);
    setSerials(pre);
    setParts(HW_MODS.map(m => m.id));
    setMode('mark');
    setInput(''); setPasteText(''); setPasteOpen(false); setFeedback(null); setResult(null); setCopied(null);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const findDev = (serial) => devices.find(d => (d.serial || '').toLowerCase() === serial.toLowerCase());

  // One row per scanned serial. `apply` is the subset of the chosen parts that this
  // device actually needs changing: missing parts when marking, present ones when clearing.
  const rows = useMemo(() => serials.map(serial => {
    const dev = findDev(serial);
    if (!dev) return { serial, dev: null, state: 'unknown', apply: [], why: 'No device with this serial is in stock.' };
    const apply = parts.filter(p => (mode === 'mark' ? !hwModDone(dev, p) : hwModDone(dev, p)));
    if (!apply.length) {
      if (mode === 'clear') return { serial, dev, state: 'done', apply, why: 'Nothing recorded for the selected parts.' };
      const recs = parts.map(p => dev.hwMods[p]);
      const first = recs[0];
      return { serial, dev, state: 'done', apply, why: `Already recorded ${fmt(first.date)}${first.by ? ' by ' + first.by : ''}.` };
    }
    const names = apply.map(p => HW_MODS.find(m => m.id === p).label).join(' + ');
    if (dev.status === 'Retired') return { serial, dev, state: 'retired', apply, why: `Device is retired. ${mode === 'mark' ? 'Will record' : 'Will clear'} ${names}.` };
    return { serial, dev, state: 'ready', apply, why: `${mode === 'mark' ? 'Will record' : 'Will clear'} ${names}.` };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [serials, parts, mode, devices]);

  if (!open) return null;

  const actionable = rows.filter(r => r.apply.length > 0);
  const counts = rows.reduce((a, r) => { a[r.state] = (a[r.state] || 0) + 1; return a; }, {});

  const addSerials = (raw) => {
    const toks = String(raw || '').split(/[\s,;]+/).map(t => stripSerial(t.trim())).filter(Boolean);
    if (!toks.length) return;
    const seen = new Set(serials.map(s => s.toLowerCase()));
    const added = [];
    let dup = null, unknown = null;
    toks.forEach(t => {
      if (seen.has(t.toLowerCase())) { dup = t; return; }
      seen.add(t.toLowerCase());
      added.push(t);
      if (!findDev(t)) unknown = t;
    });
    if (added.length) setSerials(prev => [...prev, ...added]);
    setResult(null);
    if (unknown) { buzzError(); setFeedback({ ok: false, text: `No device found for ${unknown}` }); }
    else if (dup && !added.length) { buzzError(); setFeedback({ ok: false, text: `${dup} is already in the list` }); }
    else if (added.length) { buzzSuccess(); setFeedback({ ok: true, text: added.length === 1 ? `Added ${added[0]}` : `Added ${added.length} devices` }); }
  };

  const togglePart = (id) => setParts(prev => {
    if (prev.includes(id)) return prev.length > 1 ? prev.filter(p => p !== id) : prev;
    return HW_MODS.map(m => m.id).filter(p => p === id || prev.includes(p));
  });

  const apply = () => {
    if (!actionable.length) return;
    const now = new Date().toISOString();
    const by = account?.name || account?.username || 'Unknown';
    const byId = new Map(actionable.map(r => [r.dev.id, r.apply]));
    setSt(s => ({
      ...s,
      devices: s.devices.map(d => {
        const ps = byId.get(d.id);
        if (!ps) return d;
        const next = { ...(d.hwMods || {}) };
        ps.forEach(p => {
          if (mode === 'mark') { if (!next[p]) next[p] = { date: now, by }; }
          else delete next[p];
        });
        return { ...d, hwMods: next };
      }),
    }));
    const usedParts = [...new Set(actionable.flatMap(r => r.apply))];
    audit(mode === 'mark' ? 'DEVICE_HW_MODIFIED' : 'DEVICE_HW_MOD_CLEARED', {
      parts: usedParts.map(p => HW_MODS.find(m => m.id === p).label),
      count: actionable.length,
      serials: actionable.map(r => r.serial),
      ...(actionable.length === 1 ? { deviceId: actionable[0].dev.id, serial: actionable[0].serial, deviceType: actionable[0].dev.type } : {}),
    });
    setResult({ mode, count: actionable.length, parts: usedParts, serials: actionable.map(r => r.serial) });
    setSerials([]);
    setFeedback(null);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  // A backup of the serials in case the save to the server fails after Record: paste
  // them back into "Paste a list" to redo the batch. Joined with ", " like the stock
  // export's copy buttons, which the paste box also accepts.
  const copySerials = (list, which) => {
    const text = list.join(', ');
    const fail = () => setFeedback({ ok: false, text: "Couldn't copy to the clipboard. Click the page and try again." });
    if (!navigator.clipboard) { fail(); return; }
    navigator.clipboard.writeText(text).then(
      () => { setCopied(which); setTimeout(() => setCopied(c => (c === which ? null : c)), 1500); },
      fail,
    );
  };

  const chip = (on, onClick, label, key) => (
    <button key={key} onClick={onClick} aria-pressed={on}
      style={{ padding: '6px 14px', borderRadius: 'var(--radius-pill)', border: '1px solid ' + (on ? 'var(--brand-border)' : 'var(--border-default)'), background: on ? 'var(--brand-tint)' : 'transparent', color: on ? 'var(--brand-light)' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {on && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
      {label}
    </button>
  );

  const partNames = (ids) => ids.map(p => HW_MODS.find(m => m.id === p).label).join(' + ');

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 300, backdropFilter: 'blur(3px)' }} />
      <div role="dialog" aria-modal="true" aria-label="Record hardware upgrades"
        style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 301, background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 14, width: 'min(560px,calc(100vw - 32px))', maxHeight: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>

        {/* Header */}
        <div style={{ padding: '18px 24px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>Record hardware upgrades</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Pick the parts that were changed, then scan each device.</div>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, padding: 0, flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 12, flexShrink: 0 }}>
          {/* Parts */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} role="group" aria-label="Parts changed">
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginRight: 2 }}>Parts</span>
            {HW_MODS.map(m => chip(parts.includes(m.id), () => togglePart(m.id), m.label, m.id))}
            {canClear && (
              <>
                <div style={{ flex: 1 }} />
                <button onClick={() => { setMode(v => (v === 'mark' ? 'clear' : 'mark')); setResult(null); }} aria-pressed={mode === 'clear'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: mode === 'clear' ? 'var(--danger-text)' : 'var(--text-muted)', padding: 0, textDecoration: 'underline' }}>
                  {mode === 'clear' ? 'Back to recording' : 'Clear marks instead'}
                </button>
              </>
            )}
          </div>
          {mode === 'clear' && (
            <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 8, background: 'var(--danger-fill)', border: '1px solid var(--danger-border)', color: 'var(--danger-text)' }}>
              Clearing mode: scanned devices will have the selected parts removed from their record. Use this to correct a mistake.
            </div>
          )}

          {/* Scan input */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', background: 'var(--surface-inset)' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--brand-light)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><line x1="7" y1="12" x2="17" y2="12" /></svg>
            <input ref={inputRef} value={input} aria-label="Scan a device serial"
              placeholder="Scan or type a serial, then press Enter"
              onChange={e => {
                const v = e.target.value;
                // USB scanners that send a newline or comma suffix submit straight away.
                if (/[\n,;]/.test(v)) { addSerials(v); setInput(''); } else setInput(v);
              }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSerials(input); setInput(''); } }}
              style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, color: 'var(--text-primary)' }} />
            <button onClick={() => setPasteOpen(v => !v)} className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }}>{pasteOpen ? 'Hide list' : 'Paste a list'}</button>
          </div>
          {pasteOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} rows={4} aria-label="Paste serials"
                placeholder="Paste serials separated by new lines, commas or spaces"
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--surface-inset)', color: 'var(--text-default)', fontFamily: 'inherit', fontSize: 13, resize: 'vertical' }} />
              <div><button className="btn btn-ghost btn-sm" disabled={!pasteText.trim()} onClick={() => { addSerials(pasteText); setPasteText(''); }}>Add to list</button></div>
            </div>
          )}
          {feedback && (
            <div role="status" style={{ fontSize: 12, fontWeight: 600, color: feedback.ok ? 'var(--success-text)' : 'var(--danger-text)' }}>{feedback.text}</div>
          )}
          {result && (
            <div role="status" style={{ fontSize: 13, padding: '10px 12px', borderRadius: 8, background: 'var(--success-fill)', border: '1px solid var(--success-border)', color: 'var(--success-text)', fontWeight: 600 }}>
              {result.mode === 'mark' ? 'Recorded' : 'Cleared'} {partNames(result.parts)} on {result.count} device{result.count === 1 ? '' : 's'}. Scan the next batch or close.
              <div style={{ marginTop: 6 }}>
                <button onClick={() => copySerials(result.serials, 'result')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: 'inherit', padding: 0, textDecoration: 'underline' }}>
                  {copied === 'result' ? 'Copied!' : `Copy these ${result.count} serial${result.count === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          )}
          {rows.length > 0 && (
            <div className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {[
                [(counts.ready || 0) + (counts.retired || 0), mode === 'mark' ? 'to update' : 'to clear'],
                [counts.done || 0, mode === 'mark' ? 'already done' : 'nothing to clear'],
                [counts.unknown || 0, 'unknown'],
              ].filter(([n]) => n > 0).map(([n, l]) => `${n} ${l}`).join(' · ')}
            </div>
          )}
        </div>

        {/* Scanned list */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', margin: '10px 24px 0', border: rows.length ? '1px solid var(--border-default)' : 'none', borderRadius: 8 }}>
          {rows.slice().reverse().map(r => {
            const s = STATE_STYLE[r.state];
            return (
              <div key={r.serial} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="tnum" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {r.serial}{r.dev && <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>{r.dev.type}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>{r.why}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, color: s.c, background: s.bg, border: '1px solid ' + s.b, whiteSpace: 'nowrap' }}>{mode === 'clear' && r.state === 'ready' ? 'To clear' : s.label}</span>
                <button aria-label={'Remove ' + r.serial} onClick={() => setSerials(prev => prev.filter(x => x !== r.serial))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', padding: 2, display: 'flex' }}>&#x2715;</button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px 18px', display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexShrink: 0 }}>
          {rows.length > 0 && (
            <div style={{ marginRight: 'auto', display: 'flex', gap: 4 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => copySerials(serials, 'list')} title="Copy the scanned serials so they can be pasted back in if the save fails">
                {copied === 'list' ? 'Copied!' : `Copy serials (${serials.length})`}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setSerials([]); setFeedback(null); }}>Clear list</button>
            </div>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button className="btn btn-primary" disabled={!actionable.length} onClick={apply}
            style={mode === 'clear' && actionable.length ? { background: 'var(--danger-text)' } : undefined}>
            {mode === 'mark' ? 'Record' : 'Clear'} on {actionable.length} device{actionable.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </>
  );
}

export default HwModScanModal;
